import { useCallback } from 'react';

import { useRowContextMenu } from './RowProvider.context';

// Hook to manage the context state and provide the value for the Provider
const useContextMenu = () => {
  const { setContextMenuInfo, contextMenuInfo, isOpenedViaContextMenu, setIsOpenedViaContextMenu } =
    useRowContextMenu();

  const rightClickHandler = useCallback<
    (
      event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
      /** Optional callback to be called when the context menu is set */
      onContextSet?: () => void
    ) => void
  >(
    (event, onContextSet) => {
      const tableRow = event.currentTarget;
      const tableElement = tableRow.closest('table');

      // Check if this table is inside a modal (should always allow context menu)
      const isInsideModal = tableElement?.closest('[data-modal-content="true"]');

      if (isInsideModal) {
        // Case: Context menu should open for a table inside a modal
        event.preventDefault();
        setContextMenuInfo({ x: event.clientX, y: event.clientY, opened: true });
        return;
      }

      const openModals = document.querySelectorAll('[data-modal-content="true"]');

      if (openModals.length > 0) {
        // Case: No context menu should open for a table behind a modal
        return;
      }

      // Default Case: Context menu should open for a table.
      event.preventDefault();
      setContextMenuInfo({ x: event.clientX, y: event.clientY, opened: true });
      if (onContextSet) {
        onContextSet();
        setIsOpenedViaContextMenu(true);
      }
    },
    [setContextMenuInfo, setIsOpenedViaContextMenu]
  );

  const closeContextMenu = useCallback(() => {
    setContextMenuInfo({ x: undefined, y: undefined, opened: false });
    setIsOpenedViaContextMenu(false);
  }, [setContextMenuInfo, setIsOpenedViaContextMenu]);

  // Note: currentRowIndex needs to be provided by the component using the Provider (like TableBody)
  // It's not part of the state managed here directly.
  // We return the parts needed for the context value, plus setInfo potentially.
  return {
    info: contextMenuInfo,
    rightClickHandler,
    closeContextMenu,
    setContextMenuInfo, // Include if needed externally, otherwise remove this line
    isOpenedViaContextMenu,
    setIsOpenedViaContextMenu,
  };
};

export { useContextMenu };
